import { useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, FolderSimple, Plus, ArrowSquareOut, LinkSimpleBreak, MagnifyingGlass,
  HardDrives, Trash, Copy,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { devApi, DOC_STATE_LABEL } from './devApi';
import { NasConfigNotice } from './NasConfigNotice';

const STATE_TONE = { registered: 'success', unregistered: 'neutral', updated: 'warning' };

/**
 * 設計資料を1つ追加する。種類ごとの行は廃止し、追加の入口はこれ1つ。
 * 場所は NASパス（既定）と ファイルをアップロード の2択で、アップロードを許す種類は
 * バックエンドの services/design_doc_kinds.py が唯一の定義（kinds[].upload_allowed）。
 */
export function AddDesignDocDialog({ open, onClose, devId, kinds, nasRoots, nasMap, nasConfigError, onDone }) {
  const [kindKey, setKindKey] = useState('');
  const [location, setLocation] = useState('path');
  const [path, setPath] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [revNote, setRevNote] = useState('');
  const [file, setFile] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('new');       // new | existing
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);

  const kind = kinds.find((k) => k.key === kindKey);
  // 場所の既定は種類に依らず NASパス（Aji 判断・2026-09-16）。
  // アップロードは kinds[].upload_allowed の種類で「選べる」だけ。
  const pickKind = (v) => {
    setKindKey(v);
    setLocation('path');
    setErr(null);
  };

  const reset = () => {
    setKindKey(''); setLocation('path'); setPath(''); setName(''); setNote('');
    setRevNote(''); setFile(null); setErr(null); setQ(''); setRows([]); setMode('new');
  };
  const close = () => { reset(); onClose(); };

  const search = async (v) => {
    setQ(v);
    try { setRows(await devApi.lookupDocs(devId, v)); } catch (_e) { setRows([]); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (location === 'upload') {
        await devApi.uploadDoc(devId, file, {
          title: name.trim() || file.name,
          doc_type: kind.doc_type,
          doc_profile_key: kind.key,
        });
      } else {
        await devApi.addDesignDocPath(devId, {
          kind_key: kind.key, path: path.trim(), display_name: name.trim(),
          note: note.trim() || null, revision_note: revNote.trim() || null,
        });
      }
      toast.success('設計資料を追加しました');
      onDone();
      close();
    } catch (e) {
      setErr(e.response?.data?.detail || '追加できませんでした');
    }
    setBusy(false);
  };

  const attachExisting = async (documentId) => {
    setBusy(true);
    try {
      await devApi.attachDoc(devId, documentId, null);
      toast.success('書類を紐付けました');
      onDone();
      close();
    } catch (e) { toast.error(e.response?.data?.detail || '紐付けに失敗しました'); }
    setBusy(false);
  };

  const ok = mode === 'new' && kind && name.trim()
    && (location === 'upload' ? !!file : path.trim().length > 5);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-white sm:max-w-[560px]" data-testid="dev-design-doc-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">設計資料を追加</DialogTitle>
          <DialogDescription className="text-sm">
            社内文書から選ぶか、共有フォルダー・ファイルのパスを案件に登録します。
            パスは案件の持ち物で、社内文書にはなりません。
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 text-xs font-semibold">
          <button type="button" onClick={() => setMode('new')} data-testid="dev-design-doc-mode-new"
            className={`rounded px-2 py-1 ${mode === 'new' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
            フォルダー・ファイルのパスを追加
          </button>
          <button type="button" onClick={() => setMode('existing')} data-testid="dev-design-doc-mode-existing"
            className={`rounded px-2 py-1 ${mode === 'existing' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
            社内文書から選ぶ
          </button>
        </div>

        {mode === 'existing' ? (
          <div className="space-y-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-400" />
              <Input value={q} onChange={(e) => search(e.target.value)} className="pl-7"
                placeholder="書類名・図番で検索" data-testid="dev-design-doc-search-input" />
            </div>
            <ul className="max-h-56 divide-y divide-zinc-200 overflow-auto rounded border border-zinc-200">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    {r.document_number ? `${r.document_number} ` : ''}{r.title}
                    {/* 既存書類の種類は DMS 側の値をそのまま見せる（書き換えない） */}
                    <span className="ml-2 text-xs text-zinc-600" data-testid={`dev-design-doc-existing-type-${r.id}`}>
                      種類: {r.doc_type_label || r.doc_type || '—'}（DMSの登録どおり・変更しません）
                    </span>
                  </span>
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => attachExisting(r.id)}
                    data-testid={`dev-design-doc-attach-${r.id}`}>紐付ける</Button>
                </li>
              ))}
              {rows.length === 0 && (
                <li className="px-2 py-3 text-xs text-zinc-600">検索すると候補が出ます。</li>
              )}
            </ul>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">種類</Label>
              <Select value={kindKey} onValueChange={pickKind}>
                <SelectTrigger className="mt-1" data-testid="dev-design-doc-kind-select">
                  <SelectValue placeholder="種類を選ぶ" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {kinds.map((k) => (
                    <SelectItem key={k.key} value={k.key} data-testid={`dev-design-doc-kind-${k.key}`}>
                      {k.label}{k.upload_allowed ? '' : '（パスのみ）'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-700">場所</Label>
              <div className="mt-1 flex gap-4 text-sm">
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={location === 'path'} onChange={() => setLocation('path')}
                    data-testid="dev-design-doc-loc-path" /> NASパス
                </label>
                <label className={`flex items-center gap-1.5 ${kind && !kind.upload_allowed ? 'text-zinc-400' : ''}`}>
                  <input type="radio" checked={location === 'upload'} disabled={!kind?.upload_allowed}
                    onChange={() => setLocation('upload')} data-testid="dev-design-doc-loc-upload" />
                  ファイルをアップロード
                </label>
              </div>
              {kind && !kind.upload_allowed && (
                <p className="mt-1 text-xs text-zinc-600">
                  パスは案件に登録します（社内文書は作りません。本体は NAS に置いたまま）。
                </p>
              )}
            </div>

            {location === 'path' ? (
              <div>
                <Label className="text-xs font-semibold text-zinc-700">パス</Label>
                <Input value={path} onChange={(e) => { setPath(e.target.value); setErr(null); }}
                  className="mt-1 font-mono text-xs" data-testid="dev-design-doc-path-input"
                  placeholder={`${(nasRoots || []).map((r) => (r.drive || r)).find(Boolean) || ''}\\…`} />
                <p className="mt-1 text-xs text-zinc-600" data-testid="dev-design-doc-path-hint">
                  {/* A1a: ドライブ表記で貼っても、保存・リンクは UNC に直すので
                      割当の無い PC からも開ける。 */}
                  ネットワークドライブ（{(nasRoots || []).map((r) => (r.drive || r)).join(' / ')} 配下）でも
                  UNC（{(nasRoots || []).map((r) => (r.unc || r)).join(' / ')}）でも入力できます。
                  保存は UNC 形に直すので、割当のない PC からも開けます。
                  {(nasMap || []).map((m) => `${m.unc}\\… を貼っても ${m.drive}\\… に直して保存します。`).join('')}
                  個人PCのパスは登録できません。存在確認はしません（入力者と日時を記録します）。
                  登録後は一覧のパスをクリックでコピーし、エクスプローラーのアドレス欄に貼り付けて開きます。
                </p>
                {nasConfigError && <NasConfigNotice message={nasConfigError} />}
              </div>
            ) : (
              <div>
                <Label className="text-xs font-semibold text-zinc-700">ファイル</Label>
                <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 text-sm" data-testid="dev-design-doc-file-input" />
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-zinc-700">表示名</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
                data-testid="dev-design-doc-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">メモ（任意）</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  className="mt-1" data-testid="dev-design-doc-note-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">版・改訂メモ（任意）</Label>
                <Input value={revNote} onChange={(e) => setRevNote(e.target.value)} className="mt-1"
                  data-testid="dev-design-doc-rev-note-input" />
                <p className="mt-1 text-xs text-zinc-500">
                  ただのメモです。版の管理は DMS が持ち、試作の「作った版」には選べません。
                </p>
              </div>
            </div>

            {err && (
              <p className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-800"
                data-testid="dev-design-doc-error">{err}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="dev-design-doc-cancel-btn">閉じる</Button>
          {mode === 'new' && (
            <Button onClick={save} disabled={!ok || busy} data-testid="dev-design-doc-save-btn">
              {busy ? '追加中…' : '追加'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 必須書類（構想提案書）— 構想フォームから導出。ファイルではないのでここは変えない。 */
const RequiredRow = ({ c, devId, canEdit, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    setBusy(true);
    try {
      await devApi.uploadDoc(devId, file, { title: file.name, link_role: c.key,
        doc_type: 'specification', doc_profile_key: 'specification' });
      toast.success('書類を追加しました');
      setOpen(false); setFile(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'アップロードに失敗しました'); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`dev-doc-row-${c.key}`}>
      <FileText size={16} className="shrink-0 text-zinc-500" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-zinc-900">{c.label}</p>
        {c.documents.length > 0 ? (
          <ul className="mt-0.5 space-y-0.5">
            {c.documents.map((d) => (
              <li key={d.link_id} className="flex items-center gap-2 text-xs text-zinc-700">
                <span className="truncate">
                  {d.document_number ? `${d.document_number} ` : ''}{d.title}
                </span>
                <a href={`/documents?open=${d.document_id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-0.5 font-semibold text-teal-700 hover:underline"
                  data-testid={`dev-doc-open-${d.document_id}`}>開く <ArrowSquareOut size={11} /></a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-0.5 text-xs text-zinc-600">
            {c.state === 'registered'
              ? '構想内容が登録されています（提案書PDFの添付は任意です）'
              : 'まだ書類はありません'}
          </p>
        )}
      </div>
      <StatusBadge tone={STATE_TONE[c.state]} testid={`dev-doc-state-${c.key}`}>
        {DOC_STATE_LABEL[c.state]}
      </StatusBadge>
      {canEdit && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}
          data-testid={`dev-doc-add-${c.key}`}>
          <Plus size={13} className="mr-1" /> 書類を追加
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-[420px]" data-testid="dev-doc-required-dialog">
          <DialogHeader><DialogTitle className="text-base">{c.label}を追加</DialogTitle></DialogHeader>
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm" data-testid="dev-doc-upload-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>閉じる</Button>
            <Button onClick={upload} disabled={!file || busy} data-testid="dev-doc-upload-btn">追加</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** 共有フォルダーのパスをコピー。開くのは Explorer 側（Ctrl+L で貼り付け）。 */
const copyPath = async (path) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(path);
    } else {
      const ta = document.createElement('textarea');
      ta.value = path; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    toast.success('パスをコピーしました。エクスプローラーのアドレス欄（Ctrl+L）に貼り付けて Enter');
  } catch {
    toast.error('コピーできませんでした。パスを選んで Ctrl+C でコピーしてください');
  }
};

/**
 * 設計資料。
 * 2026-09-16: 種類ごとの11行（空の案件で約700px の枠だけ）と「共有フォルダーの参照」
 * ブロックを廃止。付いているものだけを並べ、追加の入口は1つにした。
 * NASパスの参照も DMS の書類（storage=external_path・本体なし）として持つので、
 * 一覧は1つで済む。
 */
export const DocChecklist = ({ devId, checklist, rows, kinds, nasRoots, nasMap, nasConfigError, canEdit, onChanged }) => {
  const [addOpen, setAddOpen] = useState(false);
  const required = checklist.filter((c) => c.required);
  const attached = rows || [];

  const remove = async (linkId) => {
    try {
      await devApi.deleteDesignDoc(devId, linkId);
      toast.success('一覧から外しました（アップロード済みのファイルは消えません）');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || '外せませんでした'); }
  };

  return (
    <div className="space-y-4" data-testid="dev-doc-checklist">
      <div className="rounded-lg border border-zinc-300 bg-white shadow-sm">
        <div className="border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <p className="text-sm font-bold text-zinc-800">必須書類</p>
        </div>
        <div className="divide-y divide-zinc-200">
          {required.map((c) => (
            <RequiredRow key={c.key} c={c} devId={devId} canEdit={canEdit} onChanged={onChanged} />
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-design-docs">
        <div className="flex items-center justify-between border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <div>
            <p className="text-sm font-bold text-zinc-800">設計資料</p>
            <p className="text-xs text-zinc-600">図面・回路・報告書など。未登録でも次のステップへ進めます。</p>
            <p className="text-xs text-zinc-500">
              共有フォルダーのパスはクリックでコピー → エクスプローラーのアドレス欄（Ctrl+L）に貼り付けて Enter。
            </p>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}
              data-testid="dev-design-doc-add-btn">
              <Plus size={13} className="mr-1" /> 追加
            </Button>
          )}
        </div>
        {attached.length === 0 ? (
          <p className="px-4 py-3 text-sm text-zinc-600" data-testid="dev-design-docs-empty">
            まだ書類はありません。
          </p>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {attached.map((r) => (
              <li key={r.link_id} className="flex items-start gap-3 px-4 py-2.5"
                data-testid={`dev-design-doc-${r.document_id}`}>
                {r.storage === 'external_path'
                  ? <HardDrives size={16} className="mt-0.5 shrink-0 text-zinc-500" />
                  : <FileText size={16} className="mt-0.5 shrink-0 text-zinc-500" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-900">
                    {r.document_number ? `${r.document_number} ` : ''}{r.title}
                    <span className="ml-2 text-xs font-medium text-zinc-600">{r.kind_label}</span>
                  </p>
                  {r.storage === 'external_path' ? (
                    <p className="flex items-center gap-1.5">
                      <button type="button" onClick={() => copyPath(r.path)}
                        title="クリックでコピー → エクスプローラーのアドレス欄に貼り付け"
                        className="min-w-0 truncate rounded px-1 -mx-1 text-left font-mono text-xs text-zinc-700 hover:bg-teal-50 hover:text-teal-800"
                        data-testid={`dev-design-doc-path-${r.document_id}`}>{r.path}</button>
                      <button type="button" onClick={() => copyPath(r.path)}
                        className="inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-teal-700 hover:underline"
                        data-testid={`dev-design-doc-copy-${r.document_id}`}>
                        <Copy size={12} /> コピー
                      </button>
                    </p>
                  ) : (
                    <a href={`/documents?open=${r.document_id}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-0.5 text-xs font-semibold text-teal-700 hover:underline"
                      data-testid={`dev-doc-open-${r.document_id}`}>開く <ArrowSquareOut size={11} /></a>
                  )}
                  <p className="text-xs text-zinc-600">
                    {r.revision_note ? `${r.revision_note} · ` : ''}
                    {r.added_by_name || '—'} · {(r.added_at || '').slice(0, 10)}
                    {r.note ? ` · ${r.note}` : ''}
                  </p>
                </div>
                {r.storage === 'external_path' && (
                  <StatusBadge tone="neutral" testid={`dev-design-doc-unverified-${r.document_id}`}>
                    未確認
                  </StatusBadge>
                )}
                {canEdit && (
                  <button type="button" onClick={() => remove(r.link_id)} aria-label="一覧から外す"
                    className="mt-0.5 text-zinc-500 hover:text-rose-600"
                    data-testid={`dev-design-doc-remove-${r.link_id}`}>
                    {r.storage === 'external_path' ? <Trash size={14} /> : <LinkSimpleBreak size={14} />}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AddDesignDocDialog open={addOpen} onClose={() => setAddOpen(false)} devId={devId}
        kinds={kinds || []} nasRoots={nasRoots || []} nasMap={nasMap || []}
        nasConfigError={nasConfigError || ''} onDone={onChanged} />
    </div>
  );
};

export const FolderHint = () => (
  <p className="flex items-center gap-1.5 text-xs text-zinc-600">
    <FolderSimple size={13} /> 書類の本体は 社内文書 で管理します。ここでは案件との紐付けだけを扱います。
  </p>
);
